import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Link as MuiLink, Stack, Typography } from '@mui/material';
import type { TeamsDisplayFileNode } from '@atlas/shared';
import { InsertDriveFileIcon } from '@atlas/ui';
import { api } from '@/services/api';

/** Shared text behaviour: the bubble is as narrow as 280px, so nothing may widen it. */
const textFlowSx = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
} as const;

interface TeamsFileAttachmentProps {
  node: TeamsDisplayFileNode;
  /** Needed to resolve the download — the node alone does not know its message. */
  chatId?: string;
  messageId?: string;
  /**
   * False when the token predates the `Files.*` grant of 2026-07-22. Those users
   * see the name and the SharePoint link, but no download — asking Graph would
   * only produce a 403 they cannot act on from here.
   */
  filesAvailable?: boolean;
}

/**
 * A file somebody attached to a chat message.
 *
 * Three states, in descending order of usefulness:
 *
 *  1. **Downloadable** — we can resolve it through Graph. Clicking fetches a
 *     short-lived pre-authenticated URL and hands it to the browser.
 *  2. **Openable** — no download (old token, or no attachment id), but the
 *     message carried a SharePoint address. Opens there.
 *  3. **Named only** — neither. Still worth rendering: "Bob attached
 *     Report.pdf" is information even when the file is out of reach.
 *
 * A failure drops from 1 to 3 rather than showing an error banner: the file is
 * one line of a message, and a red box about it would be louder than the
 * message itself.
 */
export function TeamsFileAttachment({
  node,
  chatId,
  messageId,
  filesAvailable,
}: TeamsFileAttachmentProps) {
  const { t } = useTranslation();
  const [isResolving, setIsResolving] = useState(false);
  const [failed, setFailed] = useState(false);

  const canDownload =
    !!filesAvailable && !!node.attachmentId && !!chatId && !!messageId && !failed;

  const handleDownload = async () => {
    if (!chatId || !messageId || !node.attachmentId || isResolving) return;
    setIsResolving(true);
    try {
      const { downloadUrl } = await api.teams.getAttachmentDownload(
        chatId,
        messageId,
        node.attachmentId,
      );
      // A new tab rather than an <a download>: the URL is cross-origin, so the
      // download attribute is ignored anyway, and letting the browser handle it
      // means a PDF previews instead of being forced to disk.
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // Deleted, moved, or a permission the user does not have. Fall back to
      // whatever link the message itself carried.
      setFailed(true);
    } finally {
      setIsResolving(false);
    }
  };

  const label = (
    <Typography variant="body2" sx={{ ...textFlowSx, minWidth: 0 }}>
      {node.name}
    </Typography>
  );

  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
      {isResolving ? (
        <CircularProgress size={16} aria-hidden />
      ) : (
        <InsertDriveFileIcon fontSize="small" aria-hidden />
      )}

      {canDownload ? (
        <MuiLink
          component="button"
          type="button"
          onClick={handleDownload}
          disabled={isResolving}
          underline="hover"
          // Named rather than tooltipped: a tooltip is invisible to a screen
          // reader following the button, and "Report.pdf" alone does not say
          // that activating it downloads anything.
          aria-label={t('teams.panel.downloadFileNamed', { name: node.name })}
          sx={{ minWidth: 0, textAlign: 'left', cursor: 'pointer' }}
        >
          {label}
        </MuiLink>
      ) : node.href ? (
        <MuiLink
          href={node.href}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ minWidth: 0 }}
        >
          {label}
        </MuiLink>
      ) : (
        label
      )}
    </Stack>
  );
}
